import { useCallback } from 'react'

import { useUnit } from 'effector-react'

import BackIcon from '@/assets/icons/icon-back.svg'
import MoreIcon from '@/assets/icons/icon-more.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $hasEditVaultPermission } from '@/models/permissions.ts'
import {
  $selectedCategory,
  setSelectedCategory,
} from '@/models/vault/categories.ts'
import { $mediaQueryParam } from '@/models/vault/medias/options.ts'
import { closeMediaCategory } from '@/models/vault/vault.ts'

import { VaultMediaSearchBox } from './vault-media-search-box'

interface Props {
  showAddMedia?: boolean
}

export const VaultMediaTitle = ({ showAddMedia = true }: Props) => {
  const hasEditVaultPermission = useUnit($hasEditVaultPermission)
  const selectedCategory = useUnit($selectedCategory)
  const mediaQuery = useUnit($mediaQueryParam)

  const handleBack = () => {
    setSelectedCategory(null)
    closeMediaCategory()
  }

  const handleAddMediaClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ADD_MEDIA,
      selectedCategoryId: selectedCategory?.id,
    })
  }, [selectedCategory?.id])

  const handleVaultRenameCategoryDialogOpen = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.VAULT_RENAME_CATEGORY,
    })
  }, [])

  const handleVaultDeleteCategoryDialogOpen = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.VAULT_DELETE_CATEGORY,
    })
  }, [])

  return (
    <div className="flex items-center border-b max-lg:border-transparent pb-4 lg:h-[52px]">
      <div className="grid flex-1 grid-cols-1 pl-0 lg:grid-cols-2 lg:pl-3">
        <div className="flex items-center">
          <div className="visible lg:hidden">
            <Button
              onClick={handleBack}
              className="text-text-default mr-2"
              variant="ghost"
            >
              <BackIcon />
            </Button>
          </div>
          <p className="text-xl font-medium max-w-full truncate">
            {mediaQuery
              ? 'Results'
              : selectedCategory
                ? selectedCategory.name
                : 'All Media'}
          </p>
          {selectedCategory &&
            hasEditVaultPermission &&
            (selectedCategory?.canUpdate || selectedCategory?.canDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger className="ml-2">
                  <MoreIcon className="h-5 w-5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-50 px-[5px] py-[8px] z-100"
                >
                  {selectedCategory.canUpdate && (
                    <DropdownMenuItem
                      onClick={handleVaultRenameCategoryDialogOpen}
                    >
                      Rename Category
                    </DropdownMenuItem>
                  )}
                  {selectedCategory.canDelete && (
                    <DropdownMenuItem
                      onClick={handleVaultDeleteCategoryDialogOpen}
                    >
                      Delete Category
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
        </div>
        <div className="mt-2 flex flex-1 flex-col items-center pl-0 lg:mt-0 lg:flex-row lg:pl-3">
          <VaultMediaSearchBox />
          {showAddMedia && hasEditVaultPermission && (
            <div className="ml-2 mt-2 w-full lg:mt-0">
              <Button
                size="sm"
                onClick={handleAddMediaClick}
                className="!ml-0 w-full shrink-0 lg:ml-3"
              >
                <PlusIcon /> Add Media
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
